import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invokeAuthenticatedFunction } from "@/lib/invoke-function";
import { useOrganization } from "@/hooks/use-organization";
import { useBillingModel } from "@/hooks/use-billing-model";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { ClientDetailSkeleton } from "@/components/skeletons";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Calendar, Mail, Phone, Plus, Package, Trash2, Edit, RefreshCcw, UserPlus, Loader2, Scale, Camera, ClipboardList, ClipboardCheck, Pencil, Dumbbell, Trophy, Flame, Zap, Apple, ListChecks, Heart, FileCheck, CreditCard, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientLTVCard } from "@/components/clients/ClientLTVCard";
import { AddMeasurementDialog, MeasurementChart, MeasurementHistoryTable } from "@/components/measurements";
import { AddSessionNotesDialog } from "@/components/session-notes";
import { PhotoUploadDialog, PhotoGallery } from "@/components/progress-photos";
import { ClientVideoCoachingCard } from "@/components/video-coaching";
import { ClientWorkouts, AvailableWorkoutsPanel } from "@/components/client-workouts";
import { StreakDisplay, AchievementsGrid } from "@/components/gamification";
import { useGamification } from "@/hooks/use-gamification";
import { ProgressionSuggestions } from "@/components/progression";
import { useProgressionSuggestions } from "@/hooks/use-progression-suggestions";
import { ChurnRiskCard } from "@/components/churn-risk";
import { useChurnRisk } from "@/hooks/use-churn-risk";
import { ClientRetentionActions } from "@/components/retention/ClientRetentionActions";
import { AIRetentionInsight } from "@/components/retention/AIRetentionInsight";
import { ComplianceScoreCard, PlateauDetectionCard, EngagementAlertsCard } from "@/components/analytics";
import { VolumeBalanceAnalytics } from "@/components/volume-balance";
import EditClientDialog from "@/components/EditClientDialog";
import { MacroCalculator } from "@/components/nutrition/MacroCalculator";
import { NutritionDashboard } from "@/components/nutrition";
import {
  ClientHabitsCard,
  HabitCalendarView,
  LifestyleTrendsChart,
  HabitComplianceCard,
} from "@/components/habits";
import { ClientCheckInsCard } from "@/components/check-ins/ClientCheckInsCard";
import { ClientConsultationsCard, ActionItemsCard } from "@/components/consultations";
import {
  ClientHealthOverview,
  SleepTrendsChart,
  HrvTrendChart,
  HeartRateTrendsChart,
  HealthAlertsCard,
} from "@/components/health-analytics";
import { TouchpointScoreCard, TouchpointHistoryList } from "@/components/touchpoint-tracking";
import { OnboardingProgressCard } from "@/components/assessments";
import { ClientWaiversCard } from "@/components/waivers";
import { MedicalClearanceCard, ClearanceRequiredBanner } from "@/components/medical-clearance";
import { ClientBillingTab } from "@/components/client-billing";
import { useStripeConnect } from "@/hooks/use-stripe-connect";
import { useInvoices } from "@/hooks/use-invoices";
import { FitnessProfileWizard, ProgramGeneratorWizard, PeriodizationViewer } from "@/components/ai-programming";
import { useClientFitnessProfile } from "@/hooks/use-client-fitness-profile";
import { useOnboardingStatus } from "@/hooks/use-onboarding-status";
import { useClientLocations } from "@/hooks/use-client-locations";
import { LocationMiniMap } from "@/components/location/LocationMiniMap";
import { ClientProgressReport } from "@/components/client-reports/ClientProgressReport";
import { MapPin } from "lucide-react";
import { format, addDays, subDays, startOfDay } from "date-fns";
import { buildSplitPaymentRows, type PaymentCadence } from "@/lib/split-payments";
import { PaymentPlanPicker } from "@/components/payments/PaymentPlanPicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { sendWebhook } from "@/lib/webhook";
import { calculateSessionsRemaining } from "@/lib/package-utils";
import { parseSessionNumber } from "@/lib/session-utils";
import { useGymMasterSync } from "@/hooks/use-gymmaster-sync";
import { usePricingVersions } from "@/hooks/use-pricing-versions";

// SECURITY: Storage key prefix for user-specific pay period settings
const PAY_PERIOD_STORAGE_KEY_PREFIX = "payPeriodSettings_";

// Clear pay period settings for a specific user (exported for logout cleanup)
export function clearPayPeriodSettings(userId: string) {
  localStorage.removeItem(`${PAY_PERIOD_STORAGE_KEY_PREFIX}${userId}`);
}

interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  current_package_size: number;
  upcoming_session_number: number;
  previous_session_number: number;
  status: string;
  notes: string | null;
  created_at: string;
  next_package_size: number | null;
  next_package_rate: number | null;
  next_package_date: string | null;
  portal_enabled: boolean;
  portal_invited_at: string | null;
  auth_user_id: string | null;
  gymmaster_member_id: string | null;
  pricing_version_id: string | null;
}

interface Session {
  id: string;
  session_number: number;
  package_size: number;
  date: string;
  cancelled: boolean;
  calendar_event_title: string | null;
}

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { organization } = useOrganization();
  const { showPackages, showSessionsRemaining } = useBillingModel();
  const { hasFeature } = useFeatureFlags();
  const { isConnected: stripeConnected } = useStripeConnect();
  const { createInvoice } = useInvoices();
  const { versions: pricingVersions, getClientVersion, loading: pricingLoading } = usePricingVersions();
  const [client, setClient] = useState<Client | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activePackage, setActivePackage] = useState<any | null>(null);
  const [reupCount, setReupCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [reupOpen, setReupOpen] = useState(false);
  const [reupPackageSize, setReupPackageSize] = useState<string>("10");
  const [reupRate, setReupRate] = useState<string>("");
  const [reupPayPeriod, setReupPayPeriod] = useState<"previous" | "current" | "next">("current");
  // Configurable N-payment plan for re-up.
  const [reupPaymentCount, setReupPaymentCount] = useState<number>(1);
  const [reupPaymentCadence, setReupPaymentCadence] = useState<PaymentCadence>("monthly");
  const [reupCreateInvoice, setReupCreateInvoice] = useState(false);
  const [reupLoading, setReupLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [progressReportOpen, setProgressReportOpen] = useState(false);
  const { primaryLocation } = useClientLocations(id);
  const [sendingPortalInvite, setSendingPortalInvite] = useState(false);
  const [newSession, setNewSession] = useState({
    session_number: 1,
    package_size: 0,
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    cancelled: false,
  });

  // Session pagination
  const [sessionsPageSize, setSessionsPageSize] = useState<number>(10);

  // Measurements state
  const [measurements, setMeasurements] = useState<any[]>([]);
  const [measurementDialogOpen, setMeasurementDialogOpen] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<any | null>(null);

  // Progress photos state
  const [photos, setPhotos] = useState<any[]>([]);
  const [photoUploadOpen, setPhotoUploadOpen] = useState(false);

  // Session notes state
  const [sessionNotesDialogOpen, setSessionNotesDialogOpen] = useState(false);
  const [selectedSessionForNotes, setSelectedSessionForNotes] = useState<any | null>(null);
  const [sessionNotes, setSessionNotes] = useState<Record<string, any>>({});

  // Macro calculator state
  const [macroCalculatorOpen, setMacroCalculatorOpen] = useState(false);

  // SECURITY: User ID for user-specific storage keys
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Gamification data
  const { 
    streak, 
    achievements, 
    earnedAchievements, 
    getTotalPoints,
    loading: gamificationLoading 
  } = useGamification(id);

  // Progression suggestions
  const {
    suggestions: progressionSuggestions,
    loading: progressionLoading,
    dismissSuggestion
  } = useProgressionSuggestions(id);

  // Churn risk analysis
  const { riskScore: churnRiskScore, loading: churnLoading } = useChurnRisk(id);

  // AI Programming - Fitness Profile
  const {
    profile: fitnessProfile,
    loading: fitnessProfileLoading,
    refetch: refetchFitnessProfile
  } = useClientFitnessProfile(id);

  // GymMaster integration
  const {
    isEnabled: gymMasterEnabled,
    syncing: gymMasterSyncing,
    syncCancellation,
    createMember: createGymMasterMember,
    unlinkMember,
    autoSyncCancellations,
  } = useGymMasterSync();

  // Load current user ID for user-specific storage
  useEffect(() => {
    const loadUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setCurrentUserId(user.id);
      }
    };
    loadUserId();
  }, []);

  useEffect(() => {
    if (id) {
      fetchClientData();
    }
  }, [id]);

  // Auto-open reup dialog when navigated with ?reup=true
  useEffect(() => {
    if (searchParams.get("reup") === "true" && !loading && client) {
      setReupOpen(true);
      // Clear the param so refreshing doesn't re-open
      searchParams.delete("reup");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, client]);

  // Auto-open the edit dialog when navigated with ?edit=true (e.g. the Spotter
  // "add phone to text directly" nudge deep-links here).
  useEffect(() => {
    if (searchParams.get("edit") === "true" && !loading && client) {
      setEditClientOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, client]);

  // Auto-populate rate from pricing version when reup dialog opens or package size changes
  const clientPricingVersion = client ? getClientVersion(client.pricing_version_id) : null;
  const availablePackageSizes = clientPricingVersion && clientPricingVersion.tiers.length > 0
    ? clientPricingVersion.tiers.map((t) => t.package_size)
    : [10, 20, 30];

  useEffect(() => {
    if (reupOpen && clientPricingVersion && clientPricingVersion.tiers.length > 0) {
      // If current size isn't in the pricing version tiers, default to the first one
      const currentSize = parseInt(reupPackageSize);
      const hasCurrent = clientPricingVersion.tiers.some((t) => t.package_size === currentSize);
      if (!hasCurrent) {
        setReupPackageSize(String(clientPricingVersion.tiers[0].package_size));
        return; // The size change will re-trigger this effect
      }

      const tier = clientPricingVersion.tiers.find((t) => t.package_size === currentSize);
      if (tier) {
        // Auto-fill rate per session from the tier's total package price
        setReupRate((tier.price_cents / 100 / tier.package_size).toFixed(2));
      }
    }
  }, [reupPackageSize, reupOpen, clientPricingVersion]);

  const fetchClientData = async () => {
    try {
      const { data: clientData, error: clientError } = await supabase
        .from("clients")
        .select("*")
        .eq("id", id)
        .single();

      if (clientError) throw clientError;

      const { data: sessionsData, error: sessionsError } = await supabase
        .from("sessions")
        .select("*")
        .eq("client_id", id)
        .lte("date", new Date().toISOString())
        .order("date", { ascending: false });

      if (sessionsError) throw sessionsError;

      // Fetch active package
      const { data: packageData, error: packageError } = await supabase
        .from("packages")
        .select("*")
        .eq("client_id", id)
        .eq("status", "active")
        .single();

      if (packageError && packageError.code !== "PGRST116") throw packageError;

      // Fetch all packages to calculate reup count
      const { data: allPackages, error: allPackagesError } = await supabase
        .from("packages")
        .select("id")
        .eq("client_id", id);

      if (allPackagesError) throw allPackagesError;

      // Reup count is total packages - 1 (first package is not a reup)
      const reupCountValue = Math.max((allPackages?.length || 1) - 1, 0);
      setReupCount(reupCountValue);

      setClient(clientData);
      setSessions(sessionsData || []);
      setActivePackage(packageData || null);

      if (clientData && packageData) {
        // Count existing sessions for this package to determine next session number
        const { count: existingSessionCount } = await supabase
          .from("sessions")
          .select("*", { count: "exact", head: true })
          .eq("package_id", packageData.id);

        setNewSession({
          session_number: (existingSessionCount || 0) + 1,
          package_size: packageData.size,
          date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
          cancelled: false,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }

    // Load additional data
    fetchMeasurements();
    fetchPhotos();
    fetchSessionNotes();
  };

  const fetchMeasurements = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from("client_measurements")
        .select("*")
        .eq("client_id", id)
        .order("measured_at", { ascending: false });

      if (error) throw error;
      setMeasurements(data || []);
    } catch (error) {
      console.error("Error loading measurements:", error);
    }
  };

  const fetchPhotos = async () => {
    if (!id) return;
    try {
      const { data, error } = await supabase
        .from("progress_photos")
        .select("*")
        .eq("client_id", id)
        .order("photo_date", { ascending: false });

      if (error) throw error;
      setPhotos(data || []);
    } catch (error) {
      console.error("Error loading photos:", error);
    }
  };

  const fetchSessionNotes = async () => {
    if (!id) return;
    try {
      // Get all session IDs for this client
      const { data: sessionData } = await supabase
        .from("sessions")
        .select("id")
        .eq("client_id", id);

      if (!sessionData || sessionData.length === 0) return;

      const sessionIds = sessionData.map((s) => s.id);
      const { data: notesData, error } = await supabase
        .from("session_notes")
        .select("*")
        .in("session_id", sessionIds);

      if (error) throw error;

      // Convert to a map by session_id for easy lookup
      const notesMap: Record<string, any> = {};
      notesData?.forEach((note) => {
        notesMap[note.session_id] = note;
      });
      setSessionNotes(notesMap);
    } catch (error) {
      console.error("Error loading session notes:", error);
    }
  };

  const handleDeleteMeasurement = async (measurementId: string) => {
    try {
      const { error } = await supabase
        .from("client_measurements")
        .delete()
        .eq("id", measurementId);

      if (error) throw error;
      toast({ title: "Measurement deleted" });
      fetchMeasurements();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      // Get the photo to delete the storage file
      const photo = photos.find((p) => p.id === photoId);
      if (photo) {
        await supabase.storage.from("progress-photos").remove([photo.storage_path]);
      }

      const { error } = await supabase
        .from("progress_photos")
        .delete()
        .eq("id", photoId);

      if (error) throw error;
      toast({ title: "Photo deleted" });
      fetchPhotos();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleTogglePhotoVisibility = async (photoId: string, visible: boolean) => {
    try {
      const { error } = await supabase
        .from("progress_photos")
        .update({ is_visible_to_client: visible })
        .eq("id", photoId);

      if (error) throw error;
      toast({ title: visible ? "Photo now visible to client" : "Photo hidden from client" });
      fetchPhotos();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAddSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get active package for this client
      const { data: activePackage } = await supabase
        .from('packages')
        .select('*')
        .eq('client_id', client.id)
        .eq('status', 'active')
        .single();

      if (!activePackage) {
        toast({
          title: "Error",
          description: "No active package found for this client",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.from("sessions").insert([
        {
          client_id: client.id,
          package_id: activePackage.id,
          ...newSession,
          date: new Date(newSession.date).toISOString(),
          user_id: user.id,
          organization_id: organization?.id,
        },
      ]);

      if (error) throw error;

      // Note: sessions_completed and package activation are handled by database triggers:
      // - trigger_sync_package_sessions: Updates sessions_completed when session is completed
      // - trigger_auto_activate_pending_package: Activates pending package when current is depleted

      toast({
        title: "Success",
        description: "Session added successfully",
      });

      setOpen(false);
      fetchClientData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // SECURITY: Get pay period settings with user-specific storage key
  const getPayPeriodSettings = () => {
    if (currentUserId) {
      const saved = localStorage.getItem(`${PAY_PERIOD_STORAGE_KEY_PREFIX}${currentUserId}`);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (e) {
          console.error("Error parsing pay period settings:", e);
        }
      }
    }
    return {
      referenceDate: '2024-01-01'
    };
  };

  const getCurrentPayPeriod = () => {
    const today = startOfDay(new Date());
    const settings = getPayPeriodSettings();
    const referenceDate = new Date(settings.referenceDate);
    
    const daysSinceReference = Math.floor((today.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24));
    const payPeriodNumber = Math.floor(daysSinceReference / 14);
    const periodStart = addDays(referenceDate, payPeriodNumber * 14);
    const periodEnd = addDays(periodStart, 13);
    
    return { start: periodStart, end: periodEnd };
  };

  const getPayPeriodForDate = (targetDate: Date) => {
    const settings = getPayPeriodSettings();
    const referenceDate = new Date(settings.referenceDate);
    
    const daysSinceReference = Math.floor((targetDate.getTime() - referenceDate.getTime()) / (1000 * 60 * 60 * 24));
    const payPeriodNumber = Math.floor(daysSinceReference / 14);
    const periodStart = addDays(referenceDate, payPeriodNumber * 14);
    const periodEnd = addDays(periodStart, 13);
    
    return { start: periodStart, end: periodEnd };
  };

  const handleReupClient = async () => {
    if (!client || reupLoading) return;

    setReupLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const newPackageSize = parseInt(reupPackageSize);
      const rate = reupRate ? parseFloat(reupRate) : null;
      
      // Get active package to check remaining sessions
      const { data: activePackage } = await supabase
        .from('packages')
        .select('*')
        .eq('client_id', client.id)
        .eq('status', 'active')
        .single();

      const sessionsRemaining = client
        ? calculateSessionsRemaining(
            activePackage?.size ?? client.current_package_size,
            activePackage?.sessions_completed ?? client.previous_session_number
          )
        : 0;

      // Determine package status based on whether current package is finished
      const packageStatus = sessionsRemaining <= 0 ? 'active' : 'upcoming';

      // Complete the old package BEFORE inserting the new one
      // (unique constraint allows only one active package per client)
      if (sessionsRemaining <= 0 && activePackage) {
        await supabase
          .from('packages')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
          })
          .eq('id', activePackage.id);
      }

      // Create package FIRST — if this fails, no orphaned earnings
      const { data: newPackage, error: packageError } = await supabase
        .from('packages')
        .insert({
          client_id: client.id,
          size: newPackageSize,
          rate: rate,
          status: packageStatus,
          sessions_completed: 0,
          activated_at: packageStatus === 'active' ? new Date().toISOString() : null,
          user_id: user.id,
          organization_id: organization?.id,
          pricing_version_id: clientPricingVersion?.id || null,
        })
        .select()
        .single();

      if (packageError) throw packageError;

      // Create package activity entry
      await supabase
        .from('package_activities')
        .insert({
          package_id: newPackage.id,
          client_id: client.id,
          activity_type: 'purchased',
          description: `Package ${newPackageSize} bought`,
          user_id: user.id,
          organization_id: organization?.id,
        });

      // Create earnings AFTER package succeeds
      if (rate !== null && !isNaN(rate) && rate > 0) {
        const totalAmount = newPackageSize * rate;
        const currentPeriod = getCurrentPayPeriod();

        let selectedPeriod = currentPeriod;
        if (reupPayPeriod === "previous") {
          selectedPeriod = {
            start: subDays(currentPeriod.start, 14),
            end: subDays(currentPeriod.end, 14),
          };
        } else if (reupPayPeriod === "next") {
          selectedPeriod = {
            start: addDays(currentPeriod.start, 14),
            end: addDays(currentPeriod.end, 14),
          };
        }

        const numberOfPayments = reupPaymentCount;
        const notesBase = `${sessionsRemaining > 0 ? 'Pending ' : ''}Re-up package: ${newPackageSize} sessions @ $${rate}/session`;

        // Trainer-configurable N + cadence (was hardcoded 2 or 3 monthly).
        const splitRows = buildSplitPaymentRows({
          totalAmount,
          equalSplit: true,
          numberOfPayments,
          anchorDate: new Date(),
          cadence: reupPaymentCadence,
        });

        const earningsToInsert = splitRows.map((row) => {
          const period = row.paymentNumber === 1
            ? selectedPeriod
            : getPayPeriodForDate(row.date);
          return {
            category: "Personal Training Package",
            amount: row.amount,
            client_id: client.id,
            pay_period_start: format(period.start, "yyyy-MM-dd"),
            pay_period_end: format(period.end, "yyyy-MM-dd"),
            notes: numberOfPayments > 1
              ? `${notesBase} - Payment ${row.paymentNumber} of ${numberOfPayments}`
              : notesBase,
            date: row.dateIso,
            user_id: user.id,
          };
        });

        const { error: earningsError } = await supabase
          .from("earnings")
          .insert(earningsToInsert);

        if (earningsError) throw earningsError;
      }

      // Update client's package size and reset session counters if activating
      if (packageStatus === 'active') {
        await supabase
          .from('clients')
          .update({
            current_package_size: newPackageSize,
            previous_session_number: 0,
            upcoming_session_number: 1,
          })
          .eq('id', client.id);
      }

      // Create invoice if requested
      let invoiceMessage = '';
      if (reupCreateInvoice && rate && client.email) {
        try {
          const totalAmountCents = Math.round(newPackageSize * rate * 100);
          await createInvoice({
            client_id: client.id,
            invoice_type: 'package',
            description: `Training Package - ${newPackageSize} sessions`,
            subtotal_cents: totalAmountCents,
            package_size: newPackageSize,
            send_email: true,
          });
          invoiceMessage = ' Invoice sent to client.';
        } catch (invoiceError) {
          console.error('Failed to create invoice:', invoiceError);
          invoiceMessage = ' (Invoice creation failed)';
        }
      }

      const earningsMessage = rate ? ` Earnings of $${(newPackageSize * rate).toFixed(2)} recorded.` : '';
      const statusMessage = packageStatus === 'active'
        ? `Client re-upped to ${newPackageSize} sessions.${earningsMessage}${invoiceMessage}`
        : `Package queued! ${newPackageSize} sessions will activate when current package finishes. ${sessionsRemaining} sessions remaining.${earningsMessage}${invoiceMessage}`;

      toast({
        title: "Success",
        description: statusMessage,
      });

      // Send webhook only if activated immediately
      if (packageStatus === 'active') {
        await sendWebhook({
          event: 'client_reupped',
          client: client,
          previous_remaining: sessionsRemaining,
          new_package_size: newPackageSize,
          total_remaining: newPackageSize,
        });
      }

      setReupOpen(false);
      setReupRate("");
      setReupCreateInvoice(false);
      fetchClientData();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setReupLoading(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!client) return;

    try {
      const { error } = await supabase
        .from("clients")
        .delete()
        .eq("id", client.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Client deleted successfully",
      });

      // Send webhook notification
      await sendWebhook({
        event: 'client_deleted',
        client: client,
      });

      // Navigate back to clients list
      navigate("/clients");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSendPortalInvite = async () => {
    if (!client) return;

    if (!client.email) {
      toast({
        title: "Email Required",
        description: "Please add an email address for this client before sending a portal invite.",
        variant: "destructive",
      });
      return;
    }

    setSendingPortalInvite(true);

    try {
      const { data, error } = await invokeAuthenticatedFunction("send-portal-invite", {
        body: { client_id: client.id },
      });

      if (error) {
        toast({
          title: "Couldn't Send Invite",
          description: error,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Invite Sent",
        description: data?.message || `Portal invitation sent to ${client.email}`,
      });

      // Refresh client data to update portal status
      const { data: updatedClient } = await supabase
        .from("clients")
        .select("portal_enabled, portal_invited_at, auth_user_id")
        .eq("id", client.id)
        .single();

      if (updatedClient) {
        setClient((prev) => prev ? { ...prev, ...updatedClient } : null);
      }
    } catch (error: any) {
      console.error("Error sending portal invite:", error);
      toast({
        title: "Something Went Wrong",
        description: "Couldn't send the portal invite. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSendingPortalInvite(false);
    }
  };

  if (loading) {
    return <ClientDetailSkeleton />;
  }

  if (!client) {
    return (
      <>
        <div className="flex items-center justify-center h-[50vh]">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">Client not found</p>
            <Button onClick={() => navigate("/clients")}>Back to Clients</Button>
          </div>
        </div>
      </>
    );
  }

  const sessionsRemaining = calculateSessionsRemaining(
    activePackage?.size ?? client.current_package_size,
    activePackage?.sessions_completed ?? client.previous_session_number
  );
  // "Completed" means actually done (completed_at stamped), not just past-dated.
  // A session whose date has passed but hasn't been stamped is still pending —
  // counting those would make this card disagree with packages.sessions_completed
  // and the "remaining" card (e.g. 16 completed + 17 remaining = 33 on a 30-pack).
  const completedSessions = sessions.filter((s) => !s.cancelled && s.completed_at).length;
  const cancelledSessions = sessions.filter((s) => s.cancelled).length;

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/clients")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        <div className="rounded-lg border border-border bg-card p-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <h1 className="font-sans text-2xl font-semibold text-foreground tracking-tight">{client.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 text-sm text-muted-foreground">
              {client.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4" />
                  {client.email}
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  {client.phone}
                </div>
              )}
              {primaryLocation && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {[primaryLocation.city, primaryLocation.state].filter(Boolean).join(", ")}
                </div>
              )}
              {gymMasterEnabled && client.gymmaster_member_id && (
                <Badge variant="outline" className="gap-1 font-normal border-border bg-muted text-muted-foreground">
                  <Dumbbell className="h-3 w-3" />
                  GymMaster linked
                </Badge>
              )}
              {showPackages && pricingVersions.length > 0 && (
                <Select
                  value={client.pricing_version_id || "default"}
                  onValueChange={async (value) => {
                    const newId = value === "default" ? null : value;
                    await supabase
                      .from("clients")
                      .update({ pricing_version_id: newId })
                      .eq("id", client.id);
                    setClient({ ...client, pricing_version_id: newId });
                    toast({ title: "Pricing version updated" });
                  }}
                >
                  <SelectTrigger className="h-7 w-auto gap-1 text-xs border-dashed">
                    <CreditCard className="h-3 w-3" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default pricing</SelectItem>
                    {pricingVersions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}{v.is_default ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setEditClientOpen(true)}
              className="gap-2 border-border bg-card hover:bg-muted hover:text-foreground"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
            {showPackages && (
              <Button
                variant="outline"
                onClick={() => setReupOpen(true)}
                className="gap-2 border-border bg-card hover:bg-muted hover:text-foreground"
              >
                <Package className="h-4 w-4" />
                Re-up
              </Button>
            )}
            {hasFeature("client_portal") && (
              <Button
                variant="outline"
                onClick={handleSendPortalInvite}
                disabled={sendingPortalInvite || !client.email}
                className="gap-2 border-border bg-card hover:bg-muted hover:text-foreground"
                title={!client.email ? "Add an email address to invite to portal" : undefined}
              >
                {sendingPortalInvite ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                {client.portal_enabled
                  ? client.auth_user_id
                    ? "Resend login"
                    : "Resend invite"
                  : "Invite to portal"}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setProgressReportOpen(true)}
              className="gap-2 border-border bg-card hover:bg-muted hover:text-foreground"
            >
              <FileText className="h-4 w-4" />
              Progress report
            </Button>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(true)}
              className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add session
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add session</DialogTitle>
                <DialogDescription>Record a new session for {client.name}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddSession}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="session_number">Session Number</Label>
                    <Input
                      id="session_number"
                      type="number"
                      min={1}
                      value={newSession.session_number}
                      onChange={(e) =>
                        setNewSession({ ...newSession, session_number: parseInt(e.target.value) || 0 })
                      }
                      required
                    />
                    {newSession.session_number < 1 && (
                      <p className="text-sm text-destructive">Session number must be at least 1</p>
                    )}
                  </div>
                  {showPackages && (
                    <div className="space-y-2">
                      <Label htmlFor="package_size">Package Size</Label>
                      <Input
                        id="package_size"
                        type="number"
                        min={1}
                        value={newSession.package_size}
                        onChange={(e) =>
                          setNewSession({ ...newSession, package_size: parseInt(e.target.value) || 0 })
                        }
                        required
                      />
                      {newSession.package_size < 1 && (
                        <p className="text-sm text-destructive">Package size must be at least 1</p>
                      )}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="date">Date & Time</Label>
                    <Input
                      id="date"
                      type="datetime-local"
                      value={newSession.date}
                      onChange={(e) => setNewSession({ ...newSession, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cancelled"
                      checked={newSession.cancelled}
                      onCheckedChange={(checked) =>
                        setNewSession({ ...newSession, cancelled: checked as boolean })
                      }
                    />
                    <Label htmlFor="cancelled" className="cursor-pointer">
                      Session was cancelled
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="submit"
                    disabled={newSession.session_number < 1 || (showPackages && newSession.package_size < 1)}
                  >
                    Add session
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        </div>

        <div className={`grid gap-3 sm:gap-4 grid-cols-2 ${showPackages ? "md:grid-cols-4" : "md:grid-cols-2"}`}>
          {showSessionsRemaining && (
            <Card className="border border-border bg-card shadow-none rounded-lg">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
                <CardTitle className="font-sans text-xs font-medium text-muted-foreground">
                  Sessions remaining
                </CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div
                  className={`font-sans text-3xl font-semibold tracking-tight data-nums ${
                    sessionsRemaining < 3 ? "text-destructive" : sessionsRemaining < 5 ? "text-warning" : "text-foreground"
                  }`}
                >
                  {sessionsRemaining}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  of {client.current_package_size} total
                </p>
              </CardContent>
            </Card>
          )}

          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
              <CardTitle className="font-sans text-xs font-medium text-muted-foreground">
                Completed sessions
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="font-sans text-3xl font-semibold text-foreground tracking-tight data-nums">
                {completedSessions}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Sessions attended</p>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
              <CardTitle className="font-sans text-xs font-medium text-muted-foreground">
                Cancellations
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="font-sans text-3xl font-semibold text-foreground tracking-tight data-nums">
                {cancelledSessions}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Sessions cancelled</p>
            </CardContent>
          </Card>

          {showPackages && (
            <Card className="border border-border bg-card shadow-none rounded-lg">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
                <CardTitle className="font-sans text-xs font-medium text-muted-foreground">
                  Times re-upped
                </CardTitle>
                <RefreshCcw className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="font-sans text-3xl font-semibold text-foreground tracking-tight data-nums">
                  {reupCount}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {reupCount === 0 ? "First package" : reupCount === 1 ? "Loyal customer" : "Super loyal"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {showPackages && client.next_package_size && (
          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
              <CardTitle className="font-sans text-xs font-medium text-muted-foreground">
                Pending package
              </CardTitle>
              <Package className="h-4 w-4 text-accent" />
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="font-sans text-2xl font-semibold text-foreground tracking-tight data-nums">
                {client.next_package_size} sessions
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Will activate when the current package finishes
              </p>
            </CardContent>
          </Card>
        )}

        {gymMasterEnabled && (
          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6 pb-3">
              <div>
                <CardTitle className="font-sans text-base font-medium text-foreground tracking-tight">
                  GymMaster integration
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground mt-1">
                  {client.gymmaster_member_id
                    ? `Member ID: ${client.gymmaster_member_id}`
                    : "Not linked to GymMaster"
                  }
                </CardDescription>
              </div>
              <Dumbbell className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="flex flex-wrap gap-2">
                {client.gymmaster_member_id ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const success = await unlinkMember(client.id);
                        if (success) {
                          fetchClientData();
                        }
                      }}
                    >
                      Unlink from GymMaster
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={gymMasterSyncing}
                      onClick={async () => {
                        await syncCancellation(client.id, "Client marked inactive in TrainerPulse");
                      }}
                    >
                      {gymMasterSyncing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      Cancel membership
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    size="sm"
                    disabled={gymMasterSyncing}
                    onClick={async () => {
                      const result = await createGymMasterMember(client.id);
                      if (result.success) {
                        fetchClientData();
                      }
                    }}
                  >
                    {gymMasterSyncing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create in GymMaster
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="sessions" className="w-full">
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
            {/* Calm tab treatment: subtle card bg + foreground text on active, no shadow, no volt fill */}
            <TabsList className="w-full justify-start overflow-x-auto flex-nowrap scrollbar-hide h-auto p-1 bg-muted/40 rounded-lg">
              <TabsTrigger value="sessions" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <ClipboardList className="h-4 w-4" />
                Sessions
              </TabsTrigger>
              <TabsTrigger value="progress" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Trophy className="h-4 w-4" />
                Progress
              </TabsTrigger>
              <TabsTrigger value="measurements" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Scale className="h-4 w-4" />
                Measurements
              </TabsTrigger>
              <TabsTrigger value="photos" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Camera className="h-4 w-4" />
                Photos
              </TabsTrigger>
              <TabsTrigger value="workouts" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Dumbbell className="h-4 w-4" />
                Workouts
              </TabsTrigger>
              <TabsTrigger value="nutrition" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Apple className="h-4 w-4" />
                Nutrition
              </TabsTrigger>
              <TabsTrigger value="habits" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <ListChecks className="h-4 w-4" />
                Habits
              </TabsTrigger>
              <TabsTrigger value="check-ins" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <ClipboardCheck className="h-4 w-4" />
                Check-ins
              </TabsTrigger>
              <TabsTrigger value="health" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Heart className="h-4 w-4" />
                Health
              </TabsTrigger>
              <TabsTrigger value="programming" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <Zap className="h-4 w-4" />
                AI programming
              </TabsTrigger>
              <TabsTrigger value="onboarding" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <FileCheck className="h-4 w-4" />
                Onboarding
              </TabsTrigger>
              <TabsTrigger value="billing" className="gap-2 transition-colors duration-150 rounded-md whitespace-nowrap text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:border data-[state=active]:border-border">
                <CreditCard className="h-4 w-4" />
                Billing
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="progress" className="mt-6 space-y-6 animate-fade-up">
            {/* Compliance Score */}
            <ComplianceScoreCard clientId={client.id} />

            {/* Engagement Alerts */}
            <EngagementAlertsCard clientId={client.id} />

            {/* Churn Risk Analysis */}
            {churnRiskScore && churnRiskScore.level !== 'low' && (
              <ChurnRiskCard riskScore={churnRiskScore} />
            )}

            {/* AI Retention Coach (high/critical risk only) */}
            <AIRetentionInsight
              clientName={client.name}
              riskScore={churnRiskScore}
            />

            {/* Retention Action Items */}
            <ClientRetentionActions
              clientId={client.id}
              clientName={client.name}
              riskScore={churnRiskScore}
              organizationId={organization?.id}
            />

            {/* Plateau Detection */}
            <PlateauDetectionCard clientId={client.id} />

            {/* Smart Progression Suggestions */}
            <ProgressionSuggestions
              suggestions={progressionSuggestions}
              loading={progressionLoading}
              onDismiss={dismissSuggestion}
              onApply={(suggestion) => {
                toast({
                  title: `Progression applied: ${suggestion.exerciseName}`,
                  description: `Next session: ${suggestion.suggestedWeight}lbs (was ${suggestion.currentWeight}lbs). ${suggestion.reason}`,
                });
                dismissSuggestion(suggestion.exerciseId);
              }}
            />

            {/* Streak Stats */}
            {streak ? (
              <StreakDisplay
                currentStreak={streak.current_streak}
                longestStreak={streak.longest_streak}
                totalWorkouts={streak.total_workouts}
              />
            ) : (
              <Card className="border border-border bg-card shadow-none rounded-lg">
                <CardContent className="py-10 text-center">
                  <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center mx-auto mb-4">
                    <Flame className="h-6 w-6 text-accent" />
                  </div>
                  <h3 className="font-sans text-base font-medium text-foreground tracking-tight mb-1">
                    No workout data yet
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Assign a program to get this client logging workouts.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Achievements Grid */}
            {achievements.length > 0 && (
              <AchievementsGrid
                achievements={achievements}
                earnedAchievements={earnedAchievements}
                totalPoints={getTotalPoints()}
              />
            )}

            {/* Volume Balance Analytics */}
            <div className="space-y-4">
              <h3 className="font-sans text-base font-medium text-foreground tracking-tight">Volume balance</h3>
              <VolumeBalanceAnalytics clientId={client.id} weeksBack={4} />
            </div>
          </TabsContent>

          <TabsContent value="sessions" className="mt-6 animate-fade-up">
            <Card className="border border-border bg-card shadow-none rounded-lg">
              <CardHeader className="p-6 pb-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <CardTitle className="font-sans text-base font-medium text-foreground tracking-tight">
                      Session history
                    </CardTitle>
                    <CardDescription className="text-xs text-muted-foreground mt-1">
                      {sessions.length > 0
                        ? `Showing ${Math.min(sessionsPageSize, sessions.length)} of ${sessions.length} sessions`
                        : "Complete audit log of all sessions"}
                    </CardDescription>
                  </div>
                  {sessions.length > 10 && (
                    <Select
                      value={String(sessionsPageSize)}
                      onValueChange={(val) => setSessionsPageSize(val === "all" ? sessions.length : parseInt(val))}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">Last 10</SelectItem>
                        <SelectItem value="25">Last 25</SelectItem>
                        <SelectItem value="50">Last 50</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <div className="rounded-lg border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b border-border">
                        <TableHead className="font-sans text-xs font-medium text-muted-foreground">Session #</TableHead>
                        <TableHead className="font-sans text-xs font-medium text-muted-foreground">Date</TableHead>
                        {showPackages && <TableHead className="font-sans text-xs font-medium text-muted-foreground">Package</TableHead>}
                        <TableHead className="font-sans text-xs font-medium text-muted-foreground">Status</TableHead>
                        <TableHead className="w-[100px] font-sans text-xs font-medium text-muted-foreground">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={showPackages ? 5 : 4} className="text-center py-8 text-sm text-muted-foreground">
                            No sessions recorded yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        sessions.slice(0, sessionsPageSize).map((session) => (
                          <TableRow key={session.id} className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors duration-150">
                            <TableCell className="font-sans text-sm font-medium text-foreground data-nums">
                              {(() => {
                                // Try DB row first; fall back to parsing the calendar title
                                // (often filled in even when the DB row missed the assignment).
                                const parsed = parseSessionNumber(
                                  session.calendar_event_title,
                                  session.session_number ?? 0,
                                );
                                if (parsed > 0) return parsed;
                                if (session.completed_at) {
                                  return <span className="text-muted-foreground text-xs font-normal">Unassigned</span>;
                                }
                                return <span className="text-muted-foreground text-xs font-normal">Pending</span>;
                              })()}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{format(new Date(session.date), "MMM d, yyyy h:mm a")}</TableCell>
                            {showPackages && <TableCell className="text-sm data-nums text-foreground">{session.package_size}</TableCell>}
                            <TableCell>
                              {session.cancelled ? (
                                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 font-normal">
                                  Cancelled
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-success/10 text-success border-success/20 font-normal">
                                  Completed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedSessionForNotes({
                                    id: session.id,
                                    date: format(new Date(session.date), "MMM d, yyyy"),
                                  });
                                  setSessionNotesDialogOpen(true);
                                }}
                              >
                                {sessionNotes[session.id] ? (
                                  <Pencil className="h-4 w-4" />
                                ) : (
                                  <Plus className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="measurements" className="mt-6 space-y-4 animate-fade-up">
            <div className="flex justify-between items-center">
              <h3 className="font-sans text-base font-medium text-foreground tracking-tight">Body measurements</h3>
              <Button onClick={() => setMeasurementDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add measurement
              </Button>
            </div>

            {measurements.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4">
                <MeasurementChart measurements={measurements} metric="weight" />
                <MeasurementChart measurements={measurements} metric="body_fat" />
                <MeasurementChart measurements={measurements} metric="waist" />
              </div>
            )}

            <Card className="border border-border bg-card shadow-none rounded-lg">
              <CardHeader className="p-6 pb-3">
                <CardTitle className="font-sans text-base font-medium text-foreground tracking-tight">
                  Measurement history
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0">
                <MeasurementHistoryTable
                  measurements={measurements}
                  onEdit={(m) => {
                    setEditingMeasurement(m);
                    setMeasurementDialogOpen(true);
                  }}
                  onDelete={handleDeleteMeasurement}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="photos" className="mt-6 space-y-4 animate-fade-up">
            <div className="flex justify-between items-center">
              <h3 className="font-sans text-base font-medium text-foreground tracking-tight">Progress photos</h3>
              <Button onClick={() => setPhotoUploadOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Upload photo
              </Button>
            </div>

            <PhotoGallery
              photos={photos}
              onDelete={handleDeletePhoto}
              onToggleVisibility={handleTogglePhotoVisibility}
              emptyMessage="No progress photos yet. Click 'Upload photo' to add one."
            />

            {/* Video Coaching Section */}
            <ClientVideoCoachingCard clientId={client.id} className="mt-6" />
          </TabsContent>

          <TabsContent value="workouts" className="mt-6 space-y-6 animate-fade-up">
            <ClientWorkouts clientId={client.id} clientName={client.name} />
            {/* Self-start library — phase 2 of client-initiated workouts.
                Trainer curates which workouts the client can launch
                from the portal outside the program schedule. */}
            <AvailableWorkoutsPanel clientId={client.id} clientName={client.name} />
          </TabsContent>

          <TabsContent value="nutrition" className="mt-6 animate-fade-up">
            <NutritionDashboard
              clientId={client.id}
              onConfigureClick={() => setMacroCalculatorOpen(true)}
            />
          </TabsContent>

          <TabsContent value="habits" className="mt-6 space-y-6 animate-fade-up">
            {/* Habit Assignments */}
            <ClientHabitsCard clientId={client.id} />

            {/* Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <HabitComplianceCard clientId={client.id} days={30} />
              <LifestyleTrendsChart clientId={client.id} days={30} />
            </div>

            {/* Calendar View */}
            <HabitCalendarView clientId={client.id} />
          </TabsContent>

          <TabsContent value="check-ins" className="mt-6 space-y-6 animate-fade-up">
            {/* Pending Action Items */}
            <ActionItemsCard clientId={client.id} />

            {/* Check-ins */}
            <ClientCheckInsCard clientId={client.id} />

            {/* Consultations */}
            <ClientConsultationsCard clientId={client.id} clientName={client.name} />
          </TabsContent>

          <TabsContent value="health" className="mt-6 space-y-6 animate-fade-up">
            {/* Health Overview Summary */}
            <ClientHealthOverview clientId={client.id} />

            {/* Health Alerts */}
            {organization?.id && (
              <HealthAlertsCard
                organizationId={organization.id}
                clientId={client.id}
                showClientName={false}
              />
            )}

            {/* Touchpoint Tracking */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TouchpointScoreCard clientId={client.id} />
              <TouchpointHistoryList clientId={client.id} />
            </div>

            {/* Health Trends Charts */}
            <div className="space-y-4">
              <h3 className="font-sans text-base font-medium text-foreground tracking-tight">Health trends</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SleepTrendsChart clientId={client.id} />
                <HrvTrendChart clientId={client.id} />
              </div>
              <HeartRateTrendsChart clientId={client.id} />
            </div>
          </TabsContent>

          <TabsContent value="programming" className="mt-6 space-y-6 animate-fade-up">
            {/* Fitness Profile Setup */}
            <FitnessProfileWizard
              clientId={client.id}
              clientName={client.name}
              existingProfile={fitnessProfile}
              onSave={refetchFitnessProfile}
            />

            {/* AI Program Generator */}
            {fitnessProfile && (
              <ProgramGeneratorWizard
                clientId={client.id}
                clientName={client.name}
                fitnessProfile={fitnessProfile}
              />
            )}

            {/* Periodization Overview */}
            {fitnessProfile && (
              <PeriodizationViewer clientId={client.id} />
            )}
          </TabsContent>

          <TabsContent value="onboarding" className="mt-6 space-y-6 animate-fade-up">
            {/* Onboarding Progress Overview */}
            <OnboardingProgressCard clientId={client.id} />

            {/* Medical Clearance Section */}
            <MedicalClearanceCard clientId={client.id} />

            {/* Waivers Section */}
            <ClientWaiversCard clientId={client.id} />
          </TabsContent>

          <TabsContent value="billing" className="mt-6 animate-fade-up space-y-6">
            <ClientLTVCard clientId={client.id} />
            <ClientBillingTab clientId={client.id} clientName={client.name} />
          </TabsContent>
        </Tabs>

        {/* Re-up Dialog - only shown for package billing model */}
        {showPackages && (
        <Dialog open={reupOpen} onOpenChange={setReupOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Re-up client package</DialogTitle>
              <DialogDescription>
                Add a new session package for {client.name}.
                <br />
                Current remaining sessions: <strong>{sessionsRemaining}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {clientPricingVersion && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Badge variant="outline" className="text-xs">
                    {clientPricingVersion.name}
                    {clientPricingVersion.is_default && " (Default)"}
                  </Badge>
                  <span>Rates auto-populated from pricing version</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="package-size">Package Size</Label>
                <Select value={reupPackageSize} onValueChange={setReupPackageSize}>
                  <SelectTrigger id="package-size">
                    <SelectValue placeholder="Select package size" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePackageSizes.map((size) => {
                      const tier = clientPricingVersion?.tiers.find((t) => t.package_size === size);
                      return (
                        <SelectItem key={size} value={String(size)}>
                          {size} Sessions{tier ? ` — $${(tier.price_cents / 100).toFixed(2)}` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rate">Rate per Session ($) <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input
                  id="rate"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Leave blank to skip earnings entry"
                  value={reupRate}
                  onChange={(e) => setReupRate(e.target.value)}
                />
              </div>
              {reupRate && parseFloat(reupRate) > 0 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="payPeriod">Pay Period</Label>
                    <Select value={reupPayPeriod} onValueChange={(value: "previous" | "current" | "next") => setReupPayPeriod(value)}>
                      <SelectTrigger id="payPeriod">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="previous">
                          Previous ({format(subDays(getCurrentPayPeriod().start, 14), "MMM d")} - {format(subDays(getCurrentPayPeriod().end, 14), "MMM d")})
                        </SelectItem>
                        <SelectItem value="current">
                          Current ({format(getCurrentPayPeriod().start, "MMM d")} - {format(getCurrentPayPeriod().end, "MMM d")})
                        </SelectItem>
                        <SelectItem value="next">
                          Next ({format(addDays(getCurrentPayPeriod().start, 14), "MMM d")} - {format(addDays(getCurrentPayPeriod().end, 14), "MMM d")})
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <PaymentPlanPicker
                    count={reupPaymentCount}
                    onCountChange={setReupPaymentCount}
                    cadence={reupPaymentCadence}
                    onCadenceChange={setReupPaymentCadence}
                    totalAmount={
                      (parseInt(reupPackageSize) || 0) * (parseFloat(reupRate) || 0)
                    }
                    equalSplit
                  />
                </>
              )}

              {/* Create Invoice Option */}
              {stripeConnected && reupRate && parseFloat(reupRate) > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reupCreateInvoice"
                      checked={reupCreateInvoice}
                      onCheckedChange={(checked) => setReupCreateInvoice(checked as boolean)}
                      disabled={!client?.email}
                    />
                    <label
                      htmlFor="reupCreateInvoice"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      Create invoice with payment link
                    </label>
                  </div>
                  {!client?.email && (
                    <p className="text-xs text-muted-foreground pl-6">
                      Client needs an email address to receive invoices
                    </p>
                  )}
                  {reupCreateInvoice && client?.email && (
                    <p className="text-xs text-muted-foreground pl-6">
                      Invoice will be sent to {client.email}
                    </p>
                  )}
                </div>
              )}

              <div className="rounded-md bg-muted p-3 text-sm">
                <p className="font-medium mb-1">After re-up:</p>
                <p>Package size: {reupPackageSize} sessions</p>
                <p>Total remaining: {sessionsRemaining + parseInt(reupPackageSize)} sessions</p>
                {reupRate && !isNaN(parseFloat(reupRate)) && parseFloat(reupRate) > 0 && (
                  <>
                    <p className="mt-2 font-medium text-primary">
                      Total: ${(parseInt(reupPackageSize) * parseFloat(reupRate)).toFixed(2)}
                    </p>
                    {/* Per-payment preview line. Mirrors what PaymentPlanPicker
                        renders inside the form; kept here so the "After re-up"
                        summary shows the split too. Uses reupPaymentCount (from
                        PaymentPlanPicker) — NOT reupTwoPayment/reupThreePayment,
                        which were removed in PR #199 when the picker replaced
                        the hardcoded checkboxes. The dangling refs threw
                        ReferenceError → ErrorBoundary on every keystroke that
                        made parseFloat(reupRate) > 0 (Tessa's crash, 2026-05-28). */}
                    {reupPaymentCount > 1 && (
                      <p className="text-muted-foreground">
                        ${(parseInt(reupPackageSize) * parseFloat(reupRate)).toFixed(2)} total &divide; {reupPaymentCount} payments = ${((parseInt(reupPackageSize) * parseFloat(reupRate)) / reupPaymentCount).toFixed(2)} each
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReupOpen(false)} disabled={reupLoading}>
                Cancel
              </Button>
              <Button onClick={handleReupClient} disabled={reupLoading}>
                {reupLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Processing…
                  </>
                ) : (
                  "Re-up client"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete client</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{client.name}</strong>?
                This will permanently remove the client and all their session history.
                This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteClient}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete client
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Client Dialog */}
        <EditClientDialog
          open={editClientOpen}
          onOpenChange={setEditClientOpen}
          onSuccess={fetchClientData}
          client={client}
        />

        {/* Progress Report Dialog */}
        <ClientProgressReport
          open={progressReportOpen}
          onOpenChange={setProgressReportOpen}
          clientId={client.id}
          clientName={client.name}
        />

        {/* Measurement Dialog */}
        <AddMeasurementDialog
          open={measurementDialogOpen}
          onOpenChange={(open) => {
            setMeasurementDialogOpen(open);
            if (!open) setEditingMeasurement(null);
          }}
          onSuccess={fetchMeasurements}
          clientId={client.id}
          existingMeasurement={editingMeasurement}
        />

        {/* Photo Upload Dialog */}
        <PhotoUploadDialog
          open={photoUploadOpen}
          onOpenChange={setPhotoUploadOpen}
          onSuccess={fetchPhotos}
          clientId={client.id}
        />

        {/* Session Notes Dialog */}
        {selectedSessionForNotes && (
          <AddSessionNotesDialog
            open={sessionNotesDialogOpen}
            onOpenChange={(open) => {
              setSessionNotesDialogOpen(open);
              if (!open) setSelectedSessionForNotes(null);
            }}
            onSuccess={fetchSessionNotes}
            sessionId={selectedSessionForNotes.id}
            sessionDate={selectedSessionForNotes.date}
            clientName={client.name}
            existingNotes={sessionNotes[selectedSessionForNotes.id] || null}
          />
        )}

        {/* Macro Calculator Dialog */}
        <MacroCalculator
          clientId={client.id}
          clientName={client.name}
          open={macroCalculatorOpen}
          onOpenChange={setMacroCalculatorOpen}
        />
      </div>
    </>
  );
};

export default ClientDetail;
